import React from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { AiSuggestionItem } from '../../../api/publicSessionApi';
import { PrefillStatus } from '../hooks/usePublicSharePrefill';
import { TranslateFn } from '../hooks/useSessionFormI18n';
import { SuggestionValuePreview, humanizeFieldLabel } from './SuggestionValuePreview';

export interface SuggestionReviewBannerProps {
    status: PrefillStatus;
    suggestions: AiSuggestionItem[];
    accepted: Record<string, boolean>;
    errors: string[];
    processedBy: string | null;
    onToggle: (field: string, accepted: boolean) => void;
    onAcceptAll: () => void;
    onRejectAll: () => void;
    onApply: () => void;
    onDismiss: () => void;
    t: TranslateFn;
}

export const SuggestionReviewBanner: React.FC<SuggestionReviewBannerProps> = ({
    status,
    suggestions,
    accepted,
    errors,
    processedBy,
    onToggle,
    onAcceptAll,
    onRejectAll,
    onApply,
    onDismiss,
    t,
}) => {
    if (status === 'parsing') {
        return (
            <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <CircularProgress size={28} />
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {t('suggestions_parsing_title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('suggestions_parsing_body')}
                        </Typography>
                    </Box>
                </Stack>
            </Paper>
        );
    }

    if (status === 'error') {
        return (
            <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
                <Stack spacing={2}>
                    <Alert severity="warning">
                        {errors[0] ?? t('suggestions_error_fallback')}
                    </Alert>
                    <Stack direction="row" justifyContent="flex-end">
                        <Button variant="contained" onClick={onDismiss}>
                            {t('suggestions_continue_manually')}
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        );
    }

    if (status !== 'review' && status !== 'applying') {
        return null;
    }

    const acceptedCount = suggestions.filter((s) => accepted[s.field]).length;
    const reviewTitleKey =
        suggestions.length === 1 ? 'suggestions_review_title' : 'suggestions_review_title_plural';
    const applyKey =
        acceptedCount === 1 ? 'suggestions_btn_apply' : 'suggestions_btn_apply_plural';

    return (
        <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 3 }}>
            <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <AutoFixHighIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t(reviewTitleKey, { count: suggestions.length })}
                    </Typography>
                    {processedBy && (
                        <Chip size="small" label={processedBy} variant="outlined" />
                    )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    {t('suggestions_review_body')}
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button size="small" onClick={onAcceptAll}>
                        {t('suggestions_accept_all')}
                    </Button>
                    <Button size="small" onClick={onRejectAll}>
                        {t('suggestions_reject_all')}
                    </Button>
                </Stack>

                <Divider />

                <Stack
                    spacing={1.5}
                    sx={{
                        maxHeight: 480,
                        overflowY: 'auto',
                        pr: 1,
                    }}
                >
                    {suggestions.map((s) => {
                        const label = humanizeFieldLabel(s.label || s.field);
                        return (
                            <Stack
                                key={s.field}
                                direction="row"
                                spacing={1.5}
                                sx={{ py: 0.75, alignItems: 'flex-start' }}
                            >
                                <Checkbox
                                    checked={!!accepted[s.field]}
                                    onChange={(e) => onToggle(s.field, e.target.checked)}
                                    size="small"
                                    sx={{ mt: 0.25, p: 0.5 }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        sx={{ mb: 0.5 }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, color: 'text.primary' }}
                                        >
                                            {label}
                                        </Typography>
                                        {typeof s.confidence === 'number' && (
                                            <Chip
                                                size="small"
                                                label={`${Math.round(s.confidence * 100)}%`}
                                                variant="outlined"
                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                        )}
                                    </Stack>
                                    <SuggestionValuePreview value={s.suggestedValue} />
                                </Box>
                            </Stack>
                        );
                    })}
                </Stack>

                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 1 }}>
                    <Button
                        variant="text"
                        color="inherit"
                        onClick={onDismiss}
                        disabled={status === 'applying'}
                    >
                        {t('suggestions_btn_skip')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={onApply}
                        disabled={acceptedCount === 0 || status === 'applying'}
                    >
                        {status === 'applying' ? (
                            <CircularProgress size={20} />
                        ) : (
                            t(applyKey, { count: acceptedCount })
                        )}
                    </Button>
                </Stack>
            </Stack>
        </Paper>
    );
};
